import { UserService } from '../../../core/user.service';
import { CustomMessageService } from './../../../core/message.service';
import { TransactionActionResultType } from '../shared/models/TransactionActionResultType';
import { TransactionApprovalStatus } from '../shared/models/transaction-approval-status';
import { TransactionStatus } from '../shared/models/transaction-status';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LookupService } from '../../../shared/services/lookup.service';
import { RangeFilter } from '../../../shared/models/range.model';
import { TransactionListService } from '../shared/services/transaction-list.service';
import { TransactionListItemModel } from '../shared/models/transaction-list-item-model';
import { TransactionListFilterModel } from '../shared/models/transaction-list-filter-model';
import { PaymentMethod } from '../shared/models/payment-method';
import { ProductType } from '../shared/models/product-type';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { MessageType } from '@admin/core/models/enum/message-type';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { PortfolioService } from '@admin/portfolios/shared/services/portfolio.service';
import { PortfolioAccountManagerAdminModel } from '@admin/portfolios/shared/models/portfolio-account-manager-admin-model';
import { FeatureEnum } from '@admin/shared/models/feature-enum';
import { TransactionListTotalsModel } from '@admin/billing/transactions/shared/models/transaction-list-totals';
import { ActionResultModel } from '../../../shared/models/action-result-model';
import { PortfolioModel } from '@admin/portfolios/shared/models/portfolio-model';
import { map, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { PortfolioListRecordModel } from '@admin/portfolios/shared/models/portfolio-list-record-model';
import { ApiResponse } from '@admin/core/api-response';
import { TranslateService } from '@ngx-translate/core';
import { NewPagingWrapper } from '@admin/core/models/paging-wrapper';

@Component({
  selector: 'app-transaction-list',
  templateUrl: './transaction-list.component.html',
  styleUrls: ['./transaction-list.component.less'],
  standalone: false,
})
export class TransactionListComponent implements OnInit {
  public filterPagingWrapper: TransactionListFilterModel;

  public transactions: TransactionListItemModel[];
  public selectedTransactions: TransactionListItemModel[];

  public totalAmounts: TransactionListTotalsModel;

  public editingTransaction: TransactionListItemModel;
  public displayEdit: boolean;
  public trySendOrderConfirmation: boolean;

  public hasFinalApprovePermission: boolean;
  public hasManageAllTransactionsPermission: boolean;
  public hasEditInvoicePermission: boolean;

  public totalRecords: number;
  public pageSize: number;
  public dateFormat: string;
  public hoverDateFormat: string;
  public specialDateFormat: string;

  public accountManagers: PortfolioAccountManagerAdminModel[];

  public paymentTypeValues: SelectItem[];
  public productTypeValues: SelectItem[];

  public accessiblePortfolios: number[];

  public TransactionApprovalStatus = TransactionApprovalStatus;
  public TransactionStatus = TransactionStatus;
  public PaymentMethod = PaymentMethod;

  public isFilterCollapsed: boolean;
  private defaultDayCountFilter = 2; // TODO

  constructor(
    private lookupService: LookupService,
    private transactionService: TransactionListService,
    private portfolioService: PortfolioService,
    private route: ActivatedRoute,
    private router: Router,
    private config: ConfigurationService,
    private translateService: TranslateService,
    private messageService: CustomMessageService,
    private userService: UserService
  ) {
    const tomorrow = new Date();
    const dayBeforeTomorrow = new Date(tomorrow);
    dayBeforeTomorrow.setDate(tomorrow.getDate() - 2);
    this.filterPagingWrapper = {
      createDate: { from: dayBeforeTomorrow, to: null },
    };
    this.transactions = [];
    this.totalAmounts = null;
    this.selectedTransactions = [];
    this.editingTransaction = null;
    this.displayEdit = false;
    this.trySendOrderConfirmation = false;
    this.accountManagers = [];
    this.hasFinalApprovePermission = false;
    this.hasManageAllTransactionsPermission = false;
    this.hasEditInvoicePermission = false;
    this.isFilterCollapsed = false;
    this.hoverDateFormat = 'd/M/yy, HH:mm';
  }

  onLoad(event: LazyLoadEvent) {
    if (event.sortField && event.sortOrder) {
      this.filterPagingWrapper.sortParameters = [
        {
          column: event.sortField,
          order: event.sortOrder == 1 ? 'asc' : 'desc',
        },
      ];
    }
    this.filterPagingWrapper.start = event.first;
    this.filterPagingWrapper.limit = event.rows;
    this.onFilter();
  }

  onFilter() {
    this.selectedTransactions = [];
    this.filterPagingWrapper.start = this.filterPagingWrapper.start || 0;

    this.transactionService
      .listTransactions(this.filterPagingWrapper)
      .subscribe((t) => {
        this.totalRecords = t.data.totalCount;
        this.transactions = t.data.items;
        if (this.selectedTransactions.length > 0) {
          this.selectedTransactions = this.selectedTransactions.filter((t) =>
            this.transactions.some(
              (p) => p.transactionRecordId == t.transactionRecordId
            )
          );
        }

        this.loadTotals();
      });
  }

  private loadTotals() {
    this.transactionService
      .getTransactionsTotalAmounts(this.filterPagingWrapper)
      .subscribe((t) => {
        this.totalAmounts = t.data;
      });
  }

  onClearFilter() {
    this.filterPagingWrapper = {};
    this.filterPagingWrapper.displayZeroInvoices = true;
  }

  onApplyFilter() {
    this.filterPagingWrapper.start = 0;

    this.onFilter();
  }

  public onEditTransactionClick(transaction: TransactionListItemModel) {
    this.editingTransaction = transaction;
    this.displayEdit = true;
  }

  public onUpdateTable(updated: boolean) {
    this.displayEdit = false;
    this.trySendOrderConfirmation = false;
    if (updated) {
      this.onFilter();
    }
  }

  public onTransactionEdit(result: ActionResultModel) {
    this.displayEdit = false;
    if (result.success) {
      this.onFilter();
    } else if (result.message) {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Error,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: result.message,
      });
    }
  }

  onTransactionInitialApprove(isPartial: boolean) {
    if (this.selectedTransactions.length == 0) return;

    let ans = confirm(this.translateService.instant('message.AreYouSure'));
    if (!ans) {
      return;
    }

    this.transactionService
      .approveTransactionInitial(this.selectedTransactions, isPartial)
      .subscribe((d) => {
        if (d.resultType == TransactionActionResultType.Success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = [];
        } else if (d.resultType == TransactionActionResultType.PartialSuccess) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Warning,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = this.transactions.filter(
            (t) => d.errorMessages[t.transactionRecordId] != null
          );
        } else if (d.resultType == TransactionActionResultType.Failure) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Error,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = this.transactions.filter(
            (t) => d.errorMessages[t.transactionRecordId] != null
          );
        } else {
          throw Error('invalid data');
        }

        this.refresh();
      });
  }

  onTransactionFinalApprove() {
    if (this.selectedTransactions.length == 0) return;

    let ans = confirm(this.translateService.instant('message.AreYouSure'));
    if (!ans) {
      return;
    }

    this.transactionService
      .approveTransactionFinal(this.selectedTransactions)
      .subscribe((d) => {
        if (d.resultType == TransactionActionResultType.Success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = [];
        } else if (d.resultType == TransactionActionResultType.PartialSuccess) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Warning,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = this.transactions.filter(
            (t) => d.errorMessages[t.transactionRecordId] != null
          );
        } else if (d.resultType == TransactionActionResultType.Failure) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Error,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = this.transactions.filter(
            (t) => d.errorMessages[t.transactionRecordId] != null
          );
        } else {
          throw Error('invalid data');
        }

        this.refresh();
      });
  }

  onTransactionReject() {
    if (this.selectedTransactions.length == 0) return;

    let ans = confirm(this.translateService.instant('message.AreYouSure'));
    if (!ans) {
      return;
    }

    this.transactionService
      .rejectUnapprovedTransaction(this.selectedTransactions)
      .subscribe((d) => {
        if (d.resultType == TransactionActionResultType.Success) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = [];
        } else if (d.resultType == TransactionActionResultType.PartialSuccess) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Warning,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = this.transactions.filter(
            (t) => d.errorMessages[t.transactionRecordId] != null
          );
        } else if (d.resultType == TransactionActionResultType.Failure) {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Error,
            sourceId: ApplicationMessageSource.global,
            summary: '',
            detail: d.displayMessage,
          });
          this.selectedTransactions = this.transactions.filter(
            (t) => d.errorMessages[t.transactionRecordId] != null
          );
        } else {
          throw Error('invalid data');
        }

        this.refresh();
      });
  }

  onOrderConfirmationClick() {
    this.trySendOrderConfirmation = true;
  }

  getSelectedTransactionIds() {
    return this.selectedTransactions.map((t) => t.transactionRecordId);
  }

  refresh() {
    this.onFilter();
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  getRowStyleClass(transaction: TransactionListItemModel) {
    return transaction.transactionStatusId == TransactionStatus.Canceled ||
      transaction.transactionStatusId == TransactionStatus.Reversed ||
      transaction.transactionStatusId == TransactionStatus.Rejected
      ? 'canceled-or-rejected-transaction'
      : '';
  }

  isConfirmationAvailable() {
    return (
      this.selectedTransactions.length > 0 &&
      this.selectedTransactions.every(
        (t) =>
          t.transactionStatusId == TransactionStatus.Processing ||
          t.transactionStatusId == TransactionStatus.Complete
      )
    );
  }

  ngOnInit() {
    const config = this.config.getCachedConfig();
    this.pageSize = config.defaultPageSize;
    this.dateFormat = config.dateFormat;
    this.specialDateFormat = this.getSpecialDateFormat(this.dateFormat);
    this.initDropdownArrays();
    this.filterPagingWrapper.displayZeroInvoices = true;
    this.hasFinalApprovePermission = this.userService.currentUser.features.some(
      (f) => f == FeatureEnum.TransactionFinalApprove
    );
    this.hasManageAllTransactionsPermission =
      this.userService.currentUser.features.some(
        (f) => f == FeatureEnum.ManageAllTransactions
      );
    this.hasEditInvoicePermission = this.userService.currentUser.features.some(
      (f) => f == FeatureEnum.EditInvoice
    );

    this.route.params.subscribe((p) => {
      const transactionCode = p['code'];
      if (transactionCode) {
        this.filterPagingWrapper.transactionCode = transactionCode;
      }
    });

    combineLatest([
      this.portfolioService.getAccessiblePortfolios(),
      this.portfolioService.getPortfolios(),
    ])
      .pipe(
        map(
          ([accessiblePortfoliosResponse, allPortfoliosResponse]: [
            ApiResponse<number[]>,
            { items: PortfolioModel[]; totalCount: number }
          ]) => {
            const accessiblePortfolioIds = accessiblePortfoliosResponse.data;
            const allPortfolios = allPortfoliosResponse.items;

            const portfolioMap = allPortfolios.reduce((acc, portfolio) => {
              acc[portfolio.portfolioId] = portfolio;
              return acc;
            }, {});

            return accessiblePortfolioIds.map((id: any) => portfolioMap[id]);
          }
        )
      )
      .subscribe((accessiblePortfolios) => {
        this.accessiblePortfolios = accessiblePortfolios;
      });
  }

  initDropdownArrays() {
    this.paymentTypeValues = [
      {
        label: this.translateService.instant('label.Barter'),
        value: PaymentMethod.Barter,
      },
      {
        label: this.translateService.instant('label.CreditPaid'),
        value: PaymentMethod.CreditPaid,
      },
      {
        label: this.translateService.instant('label.Internal'),
        value: PaymentMethod.Internal,
      },
      {
        label: this.translateService.instant('label.Prepaid'),
        value: PaymentMethod.Prepaid,
      },
    ];
    this.productTypeValues = [
      {
        label: this.translateService.instant('label.Announcment'),
        value: ProductType.Announcment,
      },
      {
        label: this.translateService.instant('label.CvDatabase'),
        value: ProductType.CvDatabaseAccess,
      },
      {
        label: this.translateService.instant('label.SliderBanner'),
        value: ProductType.SliderBanner,
      },
      {
        label: this.translateService.instant('label.CvBox'),
        value: ProductType.CvBox,
      },
      {
        label: this.translateService.instant('label.Branding'),
        value: ProductType.Branding,
      },
    ];
    this.portfolioService.accountManagerList().subscribe((a) => {
      this.accountManagers = a.items;
    });
  }

  public getProductNames(transaction: TransactionListItemModel) {
    if (
      transaction.productNames == null ||
      transaction.productNames.length == 0
    ) {
      return '';
    }
    return transaction.productNames.join(' / ');
  }

  public getInitialApproveFullName(transaction: TransactionListItemModel) {
    return (
      transaction.initialCompleteApproveUserFullName ||
      transaction.initialPartialApproveUserFullName ||
      ''
    );
  }
}
